import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/stores/auth'
import { showNotification } from '@/utils/notification'

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // API returns: { success: true, data: <actual data>, meta?: {...}, timestamp: "..." }
    const apiResponse = response.data
    console.log('[API] Raw response:', apiResponse)

    if (apiResponse && typeof apiResponse === 'object' && 'data' in apiResponse) {
      // For list responses with meta (pagination), preserve both data and meta
      if ('meta' in apiResponse && apiResponse.meta) {
        console.log('[API] List response with meta:', {
          data: apiResponse.data,
          meta: apiResponse.meta,
        })
        return {
          data: apiResponse.data,
          meta: apiResponse.meta,
        }
      }
      // For single item responses, return just the data
      console.log('[API] Extracted data:', apiResponse.data)
      return apiResponse.data
    }
    console.log('[API] No data field, returning as-is')
    return apiResponse
  },
  async (error: AxiosError<{ error?: { message?: string; code?: string }; message?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response) {
      const { status, data } = error.response
      const errorMessage = data?.error?.message || data?.message || 'An error occurred'
      const errorCode = data?.error?.code

      switch (status) {
        case 401: {
          if (window.location.pathname.includes('/login')) {
            showNotification.error('Login Failed', errorMessage)
            break
          }

          const refreshToken = localStorage.getItem('refresh_token')
          if (!refreshToken || originalRequest._retry) {
            useAuthStore.getState().clearAuth()
            window.location.href = '/login'
            break
          }

          originalRequest._retry = true
          try {
            const res = await axios.post(`${apiClient.defaults.baseURL}/auth/refresh`, {
              refresh_token: refreshToken,
            })
            const newToken = res.data?.data?.token || res.data?.token
            const newRefresh = res.data?.data?.refresh_token || res.data?.refresh_token
            if (newToken) {
              localStorage.setItem('token', newToken)
              if (newRefresh) localStorage.setItem('refresh_token', newRefresh)
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return apiClient(originalRequest)
            }
            useAuthStore.getState().clearAuth()
            window.location.href = '/login'
          } catch {
            useAuthStore.getState().clearAuth()
            window.location.href = '/login'
          }
          break
        }
        case 403:
          showNotification.error(
            'Permission Denied',
            'You do not have permission to perform this action.',
          )
          break
        case 404:
          showNotification.warning('Not Found', errorMessage)
          break
        case 409:
          showNotification.warning('Conflict', errorMessage)
          break
        case 500:
          showNotification.error('Server Error', errorMessage || 'Please try again later.')
          break
        default:
          showNotification.error(errorCode || 'Error', errorMessage)
      }
    } else if (error.request) {
      showNotification.error('Network Error', 'Please check your connection.')
    }

    return Promise.reject(error)
  },
)

// Generic request function with type safety
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  return apiClient.request<unknown, T>(config)
}

// Helper to extract error message from API errors
export function getAPIErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error?: { message?: string }; message?: string }>
    if (axiosError.response?.data) {
      return (
        axiosError.response.data.error?.message ||
        axiosError.response.data.message ||
        'An error occurred'
      )
    }
    if (axiosError.message) {
      return axiosError.message
    }
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unknown error occurred'
}

// Convenience methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'GET', url }),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'POST', url, data }),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PUT', url, data }),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PATCH', url, data }),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'DELETE', url }),
}
